/**
 * `create_patient` — context-aware opener for the Create Patient form (Milestone
 * P). The form lives on TWO surfaces: the dashboard's AddPatientCard (inline,
 * present when that widget is selected) and the patients-list create modal.
 *
 * Deterministic, context-aware reach:
 *   - if the Create Patient form is ALREADY present (dashboard widget on screen,
 *     or a modal already open) -> no navigation; the form is here to fill.
 *   - otherwise -> navigate to the PATIENTS LIST and open the create modal
 *     (via `state.createNewPatient`). This is the canonical create destination
 *     AND the fallback when the dashboard widget has been removed.
 *
 * The backend `create_patient` handler then HOLDS until the `create_patient`
 * protocol actually lands (await_protocol_settled) before reporting `form_ready`
 * + `landed_context`, so the model never fills before the form has rendered.
 *
 * READ + global (registered in NokiCopilot), like useCopilotNavigation.
 */

import { useLocation, useNavigate } from "react-router-dom";

import { useCopilotAction } from "./actionRegistry";
import { getMergedPageContext } from "./pageContextRegistry";

const PATIENTS_LIST_PATH = "/patients/list";
const CREATE_PATIENT_PAGE_ID = "create_patient";

// Is the Create Patient form already on screen (dashboard widget, or an
// already-open modal)? Read the merged page-context's active protocol stack.
export function createFormAlreadyPresent() {
  try {
    const merged = getMergedPageContext();
    return Boolean(
      merged?.activeProtocols?.some((p) => p.pageId === CREATE_PATIENT_PAGE_ID),
    );
  } catch {
    return false;
  }
}

const CREATE_PATIENT_DESCRIPTOR = Object.freeze({
  name: "create_patient",
  description:
    "Implemented by noki-fe (open the Create Patient form — the dashboard widget "
    + "if present, else the patients-list create modal).",
  classification: "READ",
  parameters: [],
});

export function useCopilotCreatePatientAction() {
  const navigate = useNavigate();
  const location = useLocation();

  useCopilotAction(CREATE_PATIENT_DESCRIPTOR, async () => {
    const alreadyPresent = createFormAlreadyPresent();
    // eslint-disable-next-line no-console
    console.info("[copilot] create_patient.invoke", {
      currentPath: location?.pathname,
      alreadyPresent,
    });

    if (alreadyPresent) {
      // Form is on screen (dashboard widget, or a modal already open) — don't
      // navigate; the backend confirms readiness via await_protocol_settled.
      return {
        navigated: false,
        next_step:
          "The Create Patient form is open. Fill it with ONE set_patient_fields "
          + "call, read the details back, then create_patient_submit.",
      };
    }

    // Reach the canonical create surface: patients list + open the create modal.
    navigate(PATIENTS_LIST_PATH, {
      state: { ...(location?.state || {}), createNewPatient: true },
    });
    return {
      navigated: true,
      route: PATIENTS_LIST_PATH,
      next_step:
        "Opening the Create Patient form on the patients list — wait for it to "
        + "render, then fill with ONE set_patient_fields call.",
    };
  });
}
